import React, { type ReactNode } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import classNames from 'classnames'
import { Tile } from '@mc/design-system'
import { cloudflareLoader } from '@mc/image-cloudflare'
import classes from './HeroSection.module.scss'

const HERO_IMAGE_WIDTH = 505
const HERO_IMAGE_HEIGHT = 505

// Setting the width and height to 505, it preloads the image with 1080 width
const CLOUDFLARE_PRELOAD_WIDTH = 1080
const CLOUDFLARE_PRELOAD_QUALITY = 75

export const DEFAULT_WRAPPER_CLASS_NAME =
  'flex-[4] aspect-[5/4] lg:max-w-[615px] lg:aspect-square w-full h-auto relative'

// Renders the hero poster (preload link + eager fallback image) with `children`
// (the video) as a sibling of the fade layer. Kept separate so consumers can
// render the poster + LCP preload alone, without mounting a video.
export const HeroVideoPoster = ({
  fallbackImageUrl,
  fullWidth = false,
  className = DEFAULT_WRAPPER_CLASS_NAME,
  roundedClassName = 'rounded-md',
  videoStarted = false,
  children,
}: {
  fallbackImageUrl: string
  fullWidth?: boolean
  className?: string
  roundedClassName?: string
  videoStarted?: boolean
  children?: ReactNode
}) => (
  <>
    {fallbackImageUrl && (
      <Head>
        <link
          rel='preload'
          as='image'
          href={cloudflareLoader({
            src: fallbackImageUrl,
            width: CLOUDFLARE_PRELOAD_WIDTH,
            quality: CLOUDFLARE_PRELOAD_QUALITY,
          })}
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority='high'
        />
      </Head>
    )}
    <div className={className}>
      <div
        className={classNames('relative h-full overflow-hidden', {
          [`${classes.fullWidthVideoTile} rounded-none lg:rounded-md`]:
            fullWidth,
          [roundedClassName]: !fullWidth,
        })}
      >
        <Tile aspectRatio='auto' crop>
          <div className='h-full mc-tile__component mc-tile-video absolute mc-tile-video--show inset-0'>
            <div
              className='absolute inset-0 w-full h-full'
              style={{
                zIndex: videoStarted ? 0 : 1,
                opacity: videoStarted ? 0 : 1,
                transform: videoStarted ? 'scale(1.05)' : 'scale(1)',
                transition:
                  'opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1), transform 1.2s cubic-bezier(0.4, 0, 0.2, 1), z-index 0s 1.2s',
              }}
            >
              <Image
                src={fallbackImageUrl}
                alt=''
                width={HERO_IMAGE_WIDTH}
                height={HERO_IMAGE_HEIGHT}
                className='mc-tile-image'
                priority
                loading='eager'
                fetchPriority='high'
                decoding='sync'
              />
            </div>

            {children}
          </div>
        </Tile>
      </div>
    </div>
  </>
)
